import { convertIndexesToPDFBlob } from './edit'
import { getDWO } from './script'
import type { ImageID, BufferIndex, BufferInfo, ImageURL, ImageUrlInfo } from './types'

export const removeAllImages = async (): Promise<boolean> => {
    console.debug('removeAllImages')
    const dwo = await getDWO()
    console.debug('removeAllImages dwo', dwo)
    const complete = dwo.RemoveAllImages()
    console.debug('removeAllImages complete', complete)
    return complete
}

export const removeImage = async (imageId: ImageID): Promise<boolean> => {
    console.debug('removeImage', imageId)
    const dwo = await getDWO()
    console.debug('removeImage dwo', dwo)
    const index = dwo.ImageIDToIndex(imageId)
    console.debug('removeImage index', index)
    const complete = dwo.RemoveImage(index)
    console.debug('removeImage complete', complete)
    return complete
}

export const removeImages = async (imageIds: ImageID[]): Promise<boolean> => {
    console.debug('removeImages', imageIds)
    const dwo = await getDWO()
    console.debug('removeImages dwo', dwo)
    const indexes = imageIds.map((imageId) => dwo.ImageIDToIndex(imageId))
    console.debug('removeImages indexes', indexes)
    const selected = dwo.SelectImages(indexes)
    console.debug('removeImages selected', selected)
    const complete = dwo.RemoveAllSelectedImages()
    console.debug('removeImages complete', complete)
    return complete
}

const range = (n: number): number[] => [...Array(n).keys()] // [0,1,2,3...]

export const bufferIndexesRange = async (): Promise<BufferIndex[]> => {
    console.debug('bufferIndexesRange')
    const dwo = await getDWO()
    const imagesLength = dwo.HowManyImagesInBuffer
    const rangeArray = range(imagesLength)
    console.debug('bufferIndexesRange', rangeArray)
    return rangeArray
}

export const bufferIds = async (): Promise<ImageID[]> => {
    console.debug('bufferIds')
    const dwo = await getDWO()
    const rangeArray = await bufferIndexesRange()
    const ids = rangeArray.map((idx) => dwo.IndexToImageID(idx))
    console.debug('bufferIds', ids)
    return ids
}

export const getBufferInfo = async (): Promise<BufferInfo[]> => {
    console.debug('getBufferIdInfo')
    const dwo = await getDWO()
    const rangeArray = await bufferIndexesRange()
    const info = rangeArray.map((index) => ({
        index,
        imageId: dwo.IndexToImageID(index),
    }))
    console.debug('getBufferIdInfo', info)
    return info
}

export const imageIdToBufferIndex = async (imageId: ImageID): Promise<BufferIndex> => {
    console.debug('imageIdToIndex')
    const dwo = await getDWO()
    const index = dwo.ImageIDToIndex(imageId)
    console.debug('imageIdToIndex', index)
    return index
}

export const bufferImageUrls = async (): Promise<ImageURL[]> => {
    console.debug('bufferImageUrls')
    const dwo = await getDWO()
    const rangeArray = await bufferIndexesRange()
    const urls = rangeArray.map((index) => dwo.GetImageURL(index))
    console.debug('bufferImageUrls', urls)
    return urls
}

export const isBlankImage = async (imageId: ImageID): Promise<boolean> => {
    const dwo = await getDWO()
    const index = dwo.ImageIDToIndex(imageId)

    const isBlankExpress = dwo.IsBlankImageExpress(index)
    if (isBlankExpress) {
        console.debug('isBlank Slow', { imageId, index, isBlank: isBlankExpress })
        return isBlankExpress
    }

    const isBlankSlow = dwo.IsBlankImage(index)
    console.debug('isBlank Slow', { imageId, index, isBlank: isBlankSlow })
    return isBlankSlow
}

/** Works best for thumbnails, but may work for link href as well.
 *
 * Some browsers require matching origins or this error is sent:
 * "The supplied origin is not for the specified client. Current request is therefore not allowed access"
 * If so, make sure links do not have a `rel="noreferrer"` attribute
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types/noreferrer
 */
export const getImageURL = async (imageId: ImageID): Promise<ImageUrlInfo> => {
    console.debug('getImageURL', imageId)
    const dwo = await getDWO()

    const index = await imageIdToBufferIndex(imageId)
    const imageUrl = dwo.GetImageURL(index)
    const value = { index, imageId, imageUrl }
    console.debug('getImageURL value', value)
    return value
}

/** Url to imageId as PDF blob */
export const getPdfURL = async (imageId: ImageID): Promise<ImageUrlInfo> => {
    console.debug('getImageURLSafe', imageId)

    const index = await imageIdToBufferIndex(imageId)
    const blobAsOctetStream = await convertIndexesToPDFBlob([index])
    // If URL downloads instead of opening up browser viewer,
    // this may need to create another blob with a proper mime type
    // const blobAsPdf = new Blob([blob], { type: 'application/pdf' })
    const imageUrl = URL.createObjectURL(blobAsOctetStream)
    const value = { index, imageId, imageUrl }
    console.debug('getImageURLSafe value', value)
    return value
}
